import BrowserGeolocationManager from 'Lib/browserGeolocationManager.js';
import { redirectTo } from 'Utils/window';

import * as customEvent from './events';
import { LOCATION_SEARCH_SELECT_LOCATION_COMPLETE } from 'CoreComponents/locationSearch/events';

const CLASS_NAME = {
  fetchingLocation: 'user-request-location--fetching',
  locationFetchFailure: 'user-request-location--error'
};

class UserRequestLocation {
  constructor(elementRef) {
    // Initialise attributes
    this.elementRef = elementRef;
    this.location = elementRef.dataset['location'];

    this.locationForm = elementRef.querySelector('.form-location');

    // Bind functions
    this.onGeolocationFetchStart = this.onGeolocationFetchStart.bind(this);
    this.onGeolocationFetchSuccess = this.onGeolocationFetchSuccess.bind(this);
    this.onGeolocationFetchFailure = this.onGeolocationFetchFailure.bind(this);

    this.onSelectLocationHandler = this.onSelectLocationHandler.bind(this);

    this._addEventListeners();

    // Fetch the user location on mount
    this._retrieveGeolocation();
  }

  // Event Handlers

  /**
   * Event handler when requesting a browser geolocation
   *
   * @param {Event} event - geolocation request start event.
   * */
  onGeolocationFetchStart(event) {
    this._showSpinner();
  }

  /**
   * Event handler when the browser geolocation has been fetched successfully
   *
   * @param {Event} event - geolocation fetch success event.
   * */
  onGeolocationFetchSuccess(event) {
    if (!event.detail && !event.detail.geolocation && !event.detail.location) {
      return;
    }

    this._reloadPageWith(event.detail.location, event.detail.geolocation);
  }

  /**
   * Event handler when the browser geolocation has NOT been fetched successfully
   *
   * @param {Event} _event - geolocation fetch failure event.
   * */
  onGeolocationFetchFailure(_event) {
    this._hideSpinner();
    this._showFallbackForm();
  }

  /**
   * Event handler when the user successfully select a location manually.
   *
   * @param {Event} event - custom event triggered by locationSearch.
   * */
  onSelectLocationHandler(event) {
    event.stopPropagation();

    this.locationForm.submit();
  }

  // Private

  _addEventListeners() {
    this.elementRef.addEventListener(customEvent.USER_REQUEST_LOCATION_IS_FETCHING_GEOLOCATION, this.onGeolocationFetchStart);
    this.elementRef.addEventListener(customEvent.USER_REQUEST_LOCATION_FETCH_GEOLOCATION_SUCCESS, this.onGeolocationFetchSuccess);
    this.elementRef.addEventListener(customEvent.USER_REQUEST_LOCATION_FETCH_GEOLOCATION_FAILURE, this.onGeolocationFetchFailure);

    this.locationForm && this.locationForm.addEventListener(LOCATION_SEARCH_SELECT_LOCATION_COMPLETE, this.onSelectLocationHandler);
  }

  /**
   * Retrieve the user geolocation via the browser geolocation API
   * */
  _retrieveGeolocation() {
    let fetchLocationEvent = new CustomEvent(customEvent.USER_REQUEST_LOCATION_IS_FETCHING_GEOLOCATION);
    this.elementRef.dispatchEvent(fetchLocationEvent);

    const browserGeolocationManager = new BrowserGeolocationManager();
    browserGeolocationManager.getCurrentPosition(
      (position) => this._locationFetchSuccessHandler(position),
      (error) => this._locationFetchErrorHandler(error)
    );
  }

  /**
   * When `geolocation` is fetched successfully
   *
   * @param {Object} [position] - the geolocation position
   * */
  _locationFetchSuccessHandler(position) {
    const { latitude, longitude } = position.coords;
    const geocoder = new google.maps.Geocoder();
    const latlng = { lat: latitude, lng: longitude };

    geocoder.geocode({'location': latlng }, (results, stats) => {
      let fetchLocationSuccessEvent = new CustomEvent(customEvent.USER_REQUEST_LOCATION_FETCH_GEOLOCATION_SUCCESS, {
        detail: {
          geolocation: `(${latitude}, ${longitude})`,
          location: results[0]['formatted_address']
        }
      });
      this.elementRef.dispatchEvent(fetchLocationSuccessEvent);
    });
  }

  /**
   * When `geolocation` is NOT fetched successfully
   *
   * @param {Object} [_error] - the geolocation error
   * */
  _locationFetchErrorHandler(_error) {
    let fetchLocationFailureEvent = new CustomEvent(customEvent.USER_REQUEST_LOCATION_FETCH_GEOLOCATION_FAILURE);
    this.elementRef.dispatchEvent(fetchLocationFailureEvent);
  }

  /**
   * Append the geolocation filter to the page params
   *
   * @param {String} [geolocation] - a geolocation string e.g. (10, 20)
   * */
  _reloadPageWith(location, geolocation) {
    const filterParams = {
      'filter[instant_location]': location,
      'filter[instant_geolocation]': geolocation
    };
    const queryString = new URLSearchParams(filterParams).toString();

    redirectTo(`${window.location.origin}${window.location.pathname}?${queryString}`);
  }

  _showSpinner() {
    this.elementRef.classList.add(CLASS_NAME['fetchingLocation']);
  }

  _hideSpinner() {
    this.elementRef.classList.remove(CLASS_NAME['fetchingLocation']);
  }

  _showFallbackForm() {
    this.elementRef.classList.add(CLASS_NAME['locationFetchFailure']);
  }
}

export default UserRequestLocation;



// WEBPACK FOOTER //
// ./engines/vcliq_booking/app/assets/javascripts/vcliq_booking/components/userRequestLocation/index.js